import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { useApp } from '../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'connectify_welcome_popup_dismissed_v';

const WelcomePopup = () => {
  const { user } = useApp();
  const [config, setConfig] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/welcome-popup`);
        if (cancelled) return;
        const data = res.data;
        if (!data || !data.enabled) return;
        if (user && data.show_to_logged_in === false) return;
        const version = data.version || 1;
        const dismissedKey = `${SESSION_KEY}${version}`;
        if (sessionStorage.getItem(dismissedKey) === '1') return;
        setConfig(data);
        // Small delay for smoother entrance
        setTimeout(() => setVisible(true), 150);
      } catch (e) {
        // Silent fail — popup is non-critical
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleClose = () => {
    if (config) {
      const dismissedKey = `${SESSION_KEY}${config.version || 1}`;
      sessionStorage.setItem(dismissedKey, '1');
    }
    setVisible(false);
    setTimeout(() => setConfig(null), 200);
  };

  if (!config || !visible) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200"
      data-testid="welcome-popup-overlay"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-neutral-900 rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
        data-testid="welcome-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 hover:bg-white dark:hover:bg-neutral-800 text-gray-700 dark:text-neutral-300 shadow-sm transition-colors"
          data-testid="welcome-popup-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Image */}
        {config.image_url && (
          <div className="w-full bg-gray-100 dark:bg-neutral-800" data-testid="welcome-popup-image-wrap">
            <img
              src={config.image_url}
              alt={config.title || 'Welcome'}
              className="w-full h-auto max-h-72 object-cover block"
              data-testid="welcome-popup-image"
            />
          </div>
        )}

        {/* Content */}
        <div className="p-6 sm:p-8 space-y-4">
          {config.title && (
            <h2
              className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-neutral-100 leading-tight"
              data-testid="welcome-popup-title"
            >
              {config.title}
            </h2>
          )}

          {config.body && (
            <div
              className="text-gray-700 dark:text-neutral-300 text-base leading-relaxed prose prose-sm max-w-none [&_a]:text-emerald-600 [&_a:hover]:underline [&_strong]:font-bold [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              data-testid="welcome-popup-body"
            >
              <ReactMarkdown>{config.body}</ReactMarkdown>
            </div>
          )}

          {/* CTA Button */}
          <button
            onClick={handleClose}
            className="w-full py-4 bg-black text-white rounded-xl font-bold text-lg hover:bg-gray-800 transition-colors mt-2"
            data-testid="welcome-popup-cta"
          >
            {config.button_text || "Let's Go!"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomePopup;
